import React, { useState, useCallback, useEffect } from "react";
import api from '../../lib/axios';
import { toast } from "sonner";
import { Calculator, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatCurrency, formatNumber } from "@/utils/formatters";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * QuoteCalculator - Component for calculating quote prices based on indexed types
 */
const QuoteCalculator = ({ quoteTypes, selectedType, onTypeChange, materials, aluminumSeries }) => {
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [filterMaterial, setFilterMaterial] = useState("all");
  const [filterSeries, setFilterSeries] = useState("all");

  // Filter quote types based on material and series
  const filteredQuoteTypes = quoteTypes.filter(type => 
    (filterMaterial === "all" || type.material === filterMaterial) &&
    (filterSeries === "all" || type.series === filterSeries)
  );

  const calculateQuote = useCallback(async () => {
    if (!selectedType || !width || !height) {
      setResult(null);
      return;
    }

    const w = parseFloat(width);
    const h = parseFloat(height);

    if (isNaN(w) || isNaN(h) || w <= 0 || h <= 0) {
      setResult(null);
      return;
    }

    setIsCalculating(true);
    try {
      const response = await api.post(`${API}/calculate`, {
        quote_type_id: selectedType.id,
        width: w,
        height: h
      });
      setResult(response.data);
    } catch (error) {
      toast.error('Error al calcular cotización');
    } finally {
      setIsCalculating(false);
    }
  }, [selectedType, width, height]);

  useEffect(() => {
    const timer = setTimeout(calculateQuote, 300);
    return () => clearTimeout(timer);
  }, [calculateQuote]);

  // Get available series based on filtered types
  const availableSeries = [...new Set(
    quoteTypes
      .filter(t => filterMaterial === "all" || t.material === filterMaterial)
      .filter(t => t.series)
      .map(t => t.series)
  )];

  return (
    <Card className="metric-card">
      <CardHeader className="border-b border-slate-100 p-4">
        <CardTitle className="text-lg font-medium text-slate-900 flex items-center gap-2">
          <Calculator className="w-5 h-5" />
          Calculadora de Cotización
        </CardTitle>
        <CardDescription className="text-slate-500">
          Calcula el precio estimado basado en el índice
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        <div className="space-y-4">
          {/* Material and Series Filters */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="data-label mb-2 block">Filtrar Material</label>
              <Select
                value={filterMaterial}
                onValueChange={(value) => {
                  setFilterMaterial(value);
                  setFilterSeries("all");
                  onTypeChange(null);
                }}
              >
                <SelectTrigger data-testid="calc-filter-material" className="w-full">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {materials.map((mat) => (
                    <SelectItem key={mat} value={mat}>{mat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="data-label mb-2 block">Filtrar Serie</label>
              <Select
                value={filterSeries}
                onValueChange={(value) => {
                  setFilterSeries(value);
                  onTypeChange(null);
                }}
                disabled={filterMaterial !== "Aluminio" && filterMaterial !== "all"}
              >
                <SelectTrigger data-testid="calc-filter-series" className="w-full">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {availableSeries.map((ser) => (
                    <SelectItem key={ser} value={ser}>{ser}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="data-label mb-2 block">Tipo de Elemento</label>
            <Select
              value={selectedType?.id || ""}
              onValueChange={(value) => {
                const type = quoteTypes.find(t => t.id === value);
                onTypeChange(type);
              }}
            >
              <SelectTrigger data-testid="calculator-type-select" className="w-full">
                <SelectValue placeholder="Selecciona un tipo" />
              </SelectTrigger>
              <SelectContent>
                {filteredQuoteTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name} {type.series && `(${type.series})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedType && (
              <div className="mt-2 flex gap-2">
                <Badge variant={selectedType.material === "Aluminio" ? "default" : "secondary"}>
                  {selectedType.material}
                </Badge>
                {selectedType.series && (
                  <Badge variant="outline">{selectedType.series}</Badge>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="data-label mb-2 block">Ancho (m)</label>
              <Input
                data-testid="calculator-width"
                type="number"
                step="0.01"
                min="0"
                placeholder="Ej: 1.20"
                value={width}
                onChange={(e) => setWidth(e.target.value)}
                className="input-industrial"
              />
            </div>
            <div>
              <label className="data-label mb-2 block">Alto (m)</label>
              <Input
                data-testid="calculator-height"
                type="number"
                step="0.01"
                min="0"
                placeholder="Ej: 2.10"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                className="input-industrial"
              />
            </div>
          </div>
        </div>

        {result && (
          <div className="calculator-result" data-testid="calculator-result">
            <div className="flex items-center justify-between mb-4">
              <span className="text-slate-400 text-sm uppercase tracking-wider">Precio Estimado</span>
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            </div>
            <p className="price mb-2">{formatCurrency(result.estimated_cost)}</p>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <span>Área: {formatNumber(result.area, 4)} m²</span>
              <span>•</span>
              <span>{formatCurrency(result.average_cost_per_m2)}/m²</span>
            </div>
            <Separator className="my-4 bg-slate-700" />
            <div className="flex justify-between text-sm">
              <div>
                <p className="text-slate-400">Mínimo</p>
                <p className="font-mono font-medium text-slate-300">{formatCurrency(result.min_estimate)}</p>
              </div>
              <div className="text-right">
                <p className="text-slate-400">Máximo</p>
                <p className="font-mono font-medium text-slate-300">{formatCurrency(result.max_estimate)}</p>
              </div>
            </div>
          </div>
        )}

        {!result && selectedType && width && height && !isCalculating && (
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-800 font-medium">Valores inválidos</p>
              <p className="text-amber-600 text-sm">Ingresa dimensiones válidas mayores a 0</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QuoteCalculator;
